import "./AmountInput.scss"

import { Box } from "@mui/material"
import { BigNumber } from "ethers"
import React from "react"
import { useTranslation } from "react-i18next"
import parseStringToBigNumber, {
  formatNumber,
} from "src/utils/parseStringToBigNumber"
import Text from "./Text"
import { formatBNToString } from "src/utils"
import GradientWrapper from "./GradientWrapper"
import { useBoolean } from "src/hooks/useBoolean"
import classNames from "classnames"
import { useIsMobile } from "src/hooks/useIsMobile"

interface Props {
  value: string
  onChange?: (value: string) => void
  balance: BigNumber
  tokenSymbol?: string
  label?: string
  decimals?: number
  hidePercentageButtons?: boolean
  showMax?: boolean
  balanceLabel?: string
  error?: string | null
  hideBalance?: boolean
  suffix?: string
  maxWholeDigit?: number
  readOnly?: boolean
}

const AmountInput = ({
  value,
  onChange,
  label,
  balance,
  tokenSymbol,
  decimals = 18,
  hidePercentageButtons,
  showMax,
  balanceLabel,
  error,
  hideBalance,
  maxWholeDigit,
  readOnly,
}: Props) => {
  const { t } = useTranslation()
  const [isFocusing, handleFocusIn, handleFocusOut] = useBoolean(false)
  const isMobile = useIsMobile()

  const onPercentageInput = (value: string) => {
    const { value: valueBN } = parseStringToBigNumber(value, 2)
    const newValue = valueBN.mul(balance).div(100)
    if (value === "1") {
      onChange?.(formatBNToString(balance, decimals))
    } else {
      onChange?.(formatBNToString(newValue, decimals, 6))
    }
  }

  return (
    <Box className="amount-input">
      {!hideBalance && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Text size="small">{label}</Text>
          <Box
            sx={{
              width: "fit-content",
              borderBottom: readOnly ? "0" : "1px #adadad dashed",
              cursor: readOnly ? "inherit" : "pointer",
            }}
            onClick={() => !readOnly && onPercentageInput("1")}
          >
            <Text
              size="small"
              textAlign="right"
              sx={{ wordBreak: "break-word" }}
            >
              {balanceLabel || t("Balance")}
              {": "}
              {formatNumber(
                formatBNToString(balance, decimals),
                2,
                isMobile ? 6 : decimals,
              )}
            </Text>
          </Box>
        </Box>
      )}
      <GradientWrapper
        mt={1}
        borderRadius="10px"
        className={classNames({
          "with-error": error,
          active: isFocusing && !readOnly,
        })}
      >
        <Box
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          height={52}
        >
          <input
            readOnly={readOnly}
            onFocus={handleFocusIn}
            onBlur={handleFocusOut}
            autoComplete="off"
            autoCorrect="off"
            type="text"
            spellCheck="false"
            value={value}
            placeholder="0"
            // TODO: Extract out and add test case
            onChange={(e) => {
              // remove all chars that aren't a digit or a period
              let newValue = e.target.value.replace(/[^\d|.]/g, "")
              // disallow more than one period
              if (newValue.indexOf(".") !== newValue.lastIndexOf(".")) return
              let [whole, fraction] = newValue.split(".")
              if (maxWholeDigit && whole) {
                whole = whole.substring(0, maxWholeDigit)
              }
              if (fraction) {
                fraction = fraction.substring(0, decimals)
              }
              newValue =
                whole +
                (newValue.indexOf(".") !== -1 ? "." : "") +
                (fraction || "")
              onChange?.(newValue)
            }}
            maxLength={34}
          />
          <Box
            ml={2}
            onClick={showMax ? () => onPercentageInput("1") : undefined}
            style={showMax ? { cursor: "pointer" } : undefined}
          >
            {showMax ? "Max" : tokenSymbol}
          </Box>
        </Box>
      </GradientWrapper>
      {error && (
        <Text my={1} size="small" color="#D9475A">
          {error}
        </Text>
      )}
      {!hidePercentageButtons && (
        <Box mt={2} display="flex" alignItems="center" justifyContent="center">
          <Box
            className="percentage-button"
            onClick={() => onPercentageInput("0.25")}
          >
            25%
          </Box>
          <Box
            className="percentage-button"
            onClick={() => onPercentageInput("0.5")}
          >
            50%
          </Box>
          <Box
            className="percentage-button"
            onClick={() => onPercentageInput("0.75")}
          >
            75%
          </Box>
          <Box
            className="percentage-button"
            onClick={() => onPercentageInput("1")}
          >
            Max
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default AmountInput
